import { useState } from "react";

import { Menu, Spinner } from "./ui.jsx";
import { ChevronDown } from "lucide-react";

/**
 * One table, two renderings.
 *
 * At `md` and up it is a real table, as dense as it ever was. Below `md` each row
 * becomes a stacked card. That is not a nicety: three of the tables here were a
 * `table w-full` inside a `card overflow-hidden` with no `min-w` and no
 * `overflow-x-auto`, and a table will not shrink below its min-content width — so
 * the right-hand columns were being clipped with no way to scroll to them. On a
 * phone, Deployments lost who deployed and when.
 *
 * Columns:
 *   primary     the identity of the row; becomes the card's title
 *   hideBelow   drop this column under a breakpoint rather than forcing a scroll
 *   fill        the column that takes whatever width is left, and truncates
 *               rather than growing. An auto-layout table sizes a column to
 *               its content whatever `truncate` says, so a long commit message
 *               pushed By, When and Took off the right edge of the card and
 *               `overflow-hidden` quietly ate them — `max-w-0` is what makes
 *               the cell's content stop counting
 *   align       "right" for numbers
 *   cell(row)   render function
 *   mobileCell  a different rendering for the phone, where the line is one
 *               sentence wide and a column header is not there to explain it —
 *               Deployments puts the status glyph and the commit message on the
 *               first line, because "#77" and a "success" pill said nothing
 *   mobileOnly  keep it out of the desktop table entirely
 *
 * `actions(row)` returns `{ label, icon, onSelect, danger, busy }[]` — laid out
 * inline on desktop, collapsed into a menu on a phone, where four 26px buttons
 * in one cell were unusable anyway. A `busy` action shows a spinner where its
 * icon was and cannot be pressed again: starting a session waits on Claude for
 * up to twenty seconds, and a button that looks idle for twenty seconds gets
 * pressed twice.
 *
 * On a phone a row is one line until you tap it. Thirty-four rows each showing
 * eight fields is a very long page to scroll past to find the one you want; the
 * summary is what you scan, and the detail is one tap away. Columns marked
 * `summary: true` join the primary column on that first line.
 */

const HIDE_BELOW = {
    sm: "hidden sm:table-cell",
    md: "hidden md:table-cell",
    lg: "hidden lg:table-cell",
    xl: "hidden xl:table-cell"
};

export function DataTable({ rows, columns, rowKey, actions, onRowClick, empty = null, caption }) {
    if (rows.length === 0) return empty;

    const primary = columns.find((column) => column.primary) ?? columns[0];
    const rest = columns.filter((column) => column !== primary);
    // A phone-only column is a line in the opened row and nothing in the table.
    const table = columns.filter((column) => !column.mobileOnly);

    return (
        <>
            {/* Desktop */}
            <div className="card hidden overflow-hidden md:block">
                <table className="w-full">
                    {caption ? <caption className="sr-only">{caption}</caption> : null}
                    <thead className="border-b border-gray-200 dark:border-gray-800">
                        <tr>
                            {table.map((column) => (
                                <th
                                    key={column.key}
                                    className={`table-head ${column.align === "right" ? "text-right" : ""} ${column.fill ? "w-full" : ""} ${HIDE_BELOW[column.hideBelow] ?? ""}`}
                                >
                                    {column.header}
                                </th>
                            ))}
                            {actions ? <th className="table-head text-right">Actions</th> : null}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {rows.map((row) => (
                            <tr
                                key={rowKey(row)}
                                // The click belongs to the row. Deployments used to put it on
                                // three of its seven cells, so half the row was inert.
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                className={onRowClick ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900" : "hover:bg-gray-50 dark:hover:bg-gray-900"}
                            >
                                {table.map((column) => (
                                    <td
                                        key={column.key}
                                        className={`table-data ${column.align === "right" ? "text-right tabular-nums" : ""} ${column.fill ? "w-full max-w-0" : ""} ${HIDE_BELOW[column.hideBelow] ?? ""}`}
                                    >
                                        {column.cell(row)}
                                    </td>
                                ))}
                                {actions ? (
                                    <td className="table-data" onClick={(event) => event.stopPropagation()}>
                                        <div className="flex justify-end gap-1">
                                            {actions(row).map((action) => (
                                                <button
                                                    key={action.label}
                                                    onClick={action.onSelect}
                                                    disabled={action.busy || action.disabled}
                                                    title={action.label}
                                                    aria-label={action.label}
                                                    aria-busy={action.busy || undefined}
                                                    className={`${action.danger ? "btn-danger" : "btn-ghost"} px-2 py-1`}
                                                >
                                                    {action.busy ? <Spinner size={12} /> : action.icon ? <action.icon size={12} /> : action.label}
                                                </button>
                                            ))}
                                        </div>
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile */}
            <ul className="card divide-y divide-gray-100 md:hidden dark:divide-gray-800">
                {rows.map((row) => (
                    <MobileRow
                        key={rowKey(row)}
                        row={row}
                        primary={primary}
                        summary={columns.filter((column) => column.summary)}
                        detail={rest.filter((column) => !column.summary && !column.mobileHidden)}
                        actions={actions}
                        onRowClick={onRowClick}
                    />
                ))}
            </ul>
        </>
    );
}

function MobileRow({ row, primary, summary, detail, actions, onRowClick }) {
    const [open, setOpen] = useState(false);
    const expandable = detail.length > 0;

    return (
        <li>
            <div className="flex items-center gap-2 px-3">
                <button
                    onClick={() => (expandable ? setOpen((value) => !value) : onRowClick?.(row))}
                    aria-expanded={expandable ? open : undefined}
                    className="flex min-w-0 flex-1 items-center gap-3 py-3 text-left"
                >
                    <span className="min-w-0 flex-1">{(primary.mobileCell ?? primary.cell)(row)}</span>
                    {summary.map((column) => (
                        <span key={column.key} className="shrink-0">{(column.mobileCell ?? column.cell)(row)}</span>
                    ))}
                    {expandable ? (
                        <ChevronDown size={15} className={`muted shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
                    ) : null}
                </button>
                {actions ? <Menu items={actions(row)} /> : null}
            </div>

            {open ? (
                <dl className="space-y-1 px-3 pb-3">
                    {detail.map((column) => (
                        <div key={column.key} className="card-field">
                            <dt className="muted shrink-0 text-xs">{column.header}</dt>
                            <dd className="min-w-0 text-right">{column.cell(row)}</dd>
                        </div>
                    ))}
                    {onRowClick ? (
                        <button className="btn-ghost mt-2 w-full" onClick={() => onRowClick(row)}>Open</button>
                    ) : null}
                </dl>
            ) : null}
        </li>
    );
}
